import type { ContractSourceMeta } from '@/contracts/questionnaire'
import { z } from 'zod'

export const AdminUserStatusSchema = z.enum(['active', 'invited', 'suspended', 'locked'])
export const AdminUserRoleSchema = z.enum([
  'owner',
  'admin',
  'operator',
  'auditor',
  'support',
  'billing',
])
export const AdminUserAccessScopeSchema = z.enum([
  'workspace',
  'forms',
  'customers',
  'analytics',
  'billing',
])
export const AdminUserAuthProviderSchema = z.enum([
  'google',
  'email_magic_link',
  'sso',
  'manual_invite',
])
export const AdminDigestFrequencySchema = z.enum(['instant', 'daily', 'weekly', 'off'])
export const AdminRiskLevelSchema = z.enum(['low', 'medium', 'high'])

export const AdminPermissionsSchema = z.object({
  manageForms: z.boolean().default(false),
  publishForms: z.boolean().default(false),
  manageFolders: z.boolean().default(false),
  viewResponses: z.boolean().default(false),
  exportResponses: z.boolean().default(false),
  manageCustomers: z.boolean().default(false),
  manageAdmins: z.boolean().default(false),
  manageBilling: z.boolean().default(false),
  manageIntegrations: z.boolean().default(false),
  manageBranding: z.boolean().default(false),
  useAiTools: z.boolean().default(false),
  manageWebhooks: z.boolean().default(false),
  viewAuditLogs: z.boolean().default(false),
  deleteData: z.boolean().default(false),
  impersonateUsers: z.boolean().default(false),
})

export type AdminPermissions = z.infer<typeof AdminPermissionsSchema>

export const AdminUserContractSchema = z.object({
  id: z.string().default(''),
  email: z.string().email(),
  displayName: z.string().trim().min(1),
  legalName: z.string().default(''),
  jobTitle: z.string().default(''),
  department: z.string().default(''),
  employeeId: z.string().default(''),
  company: z.string().default('MiaoForm Internal Workspace'),
  phone: z.string().default(''),
  backupEmail: z.string().default(''),
  status: AdminUserStatusSchema.default('active'),
  role: AdminUserRoleSchema.default('admin'),
  accessScope: AdminUserAccessScopeSchema.default('workspace'),
  workspaceRole: z.string().default('admin'),
  authProvider: AdminUserAuthProviderSchema.default('google'),
  loginEmailVerified: z.boolean().default(false),
  allowEmailLogin: z.boolean().default(false),
  allowGoogleLogin: z.boolean().default(true),
  ssoSubject: z.string().default(''),
  timezone: z.string().default('Asia/Taipei'),
  language: z.string().default('繁體中文'),
  country: z.string().default('台灣'),
  city: z.string().default(''),
  managerName: z.string().default(''),
  managerEmail: z.string().default(''),
  lastLoginAt: z.string().default(''),
  lastIp: z.string().default(''),
  lastUserAgent: z.string().default(''),
  invitedAt: z.string().default(''),
  invitationExpiresAt: z.string().default(''),
  createdAt: z.string().default(''),
  permissions: AdminPermissionsSchema.default({}),
  mfaRequired: z.boolean().default(true),
  mfaEnabled: z.boolean().default(false),
  sessionTimeoutMinutes: z.number().int().positive().default(90),
  ipAllowlist: z.string().default(''),
  allowedDomains: z.string().default(''),
  deviceTrustRequired: z.boolean().default(false),
  passwordResetRequired: z.boolean().default(false),
  passwordConfigured: z.boolean().default(false),
  dataExportApprovalRequired: z.boolean().default(true),
  formScope: z.string().default('全部問卷'),
  customerScope: z.string().default('全部客戶'),
  defaultLandingPage: z.string().default('/dashboard/forms'),
  notifySecurity: z.boolean().default(true),
  notifyExports: z.boolean().default(false),
  notifyBilling: z.boolean().default(false),
  notifyNewResponses: z.boolean().default(false),
  digestFrequency: AdminDigestFrequencySchema.default('instant'),
  lastPermissionReviewAt: z.string().default(''),
  reviewedBy: z.string().default(''),
  riskLevel: AdminRiskLevelSchema.default('low'),
  auditNotes: z.string().default(''),
  internalNotes: z.string().default(''),
  updatedAt: z.string().default(''),
})

export type AdminUserStatus = z.infer<typeof AdminUserStatusSchema>
export type AdminUserRole = z.infer<typeof AdminUserRoleSchema>
export type AccessScope = z.infer<typeof AdminUserAccessScopeSchema>
export type AuthProvider = z.infer<typeof AdminUserAuthProviderSchema>
export type DigestFrequency = z.infer<typeof AdminDigestFrequencySchema>
export type AdminUserContract = z.infer<typeof AdminUserContractSchema> & {
  sourceMeta?: ContractSourceMeta
}

export const defaultAdminPermissions: AdminPermissions = {
  manageForms: true,
  publishForms: true,
  manageFolders: true,
  viewResponses: true,
  exportResponses: false,
  manageCustomers: true,
  manageAdmins: false,
  manageBilling: false,
  manageIntegrations: false,
  manageBranding: false,
  useAiTools: false,
  manageWebhooks: false,
  viewAuditLogs: true,
  deleteData: false,
  impersonateUsers: false,
}

export function createDefaultAdminUser(
  input: Partial<AdminUserContract> & Pick<AdminUserContract, 'email'>,
): AdminUserContract {
  const parsed = AdminUserContractSchema.parse({
    ...input,
    displayName: input.displayName || input.email.split('@')[0] || '管理員',
    legalName: input.legalName || input.displayName || input.email.split('@')[0] || '管理員',
    permissions: {
      ...defaultAdminPermissions,
      ...input.permissions,
    },
  })
  return parsed
}
